import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { Customer, CustomerDocument } from '@/types/customer';
import { Archive, Check, CheckSquare, Download, Edit2, FileText, FolderOpen, Plus, Search, Square, Trash2, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface DocumentsSectionProps {
  customer: Customer;
  onDocumentsUpdated?: () => void;
}

// Local-date-safe YYYY-MM-DD formatter for the native <input type="date">
// value — avoids toISOString()'s UTC shift, which can be off-by-one for
// users west of UTC.
const toLocalDateString = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Date range covering the last `days` days (inclusive of today).
const getLastNDaysRange = (days: number) => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - days);
  return { from: toLocalDateString(start), to: toLocalDateString(today) };
};

// Default date filter range: last 7 days. Customers land on their most recent
// uploads; the presets / From-To pickers below let them widen to 30 days or
// "All time" to see everything they've uploaded previously.
const getDefaultDateRange = () => getLastNDaysRange(7);

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ customer, onDocumentsUpdated }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<CustomerDocument[]>([]);
  const [documentsRequired, setDocumentsRequired] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange().from);
  const [dateTo, setDateTo] = useState(() => getDefaultDateRange().to);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      // Backend applies the role-based visibility filter, the date-range
      // filter, and returns documents newest-first.
      const response = await apiService.getCustomerDocuments(customer._id!, { dateFrom, dateTo });
      if (response.success && response.data) {
        setDocuments(response.data.documents || []);
        setDocumentsRequired(response.data.documentsRequired || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [customer._id, dateFrom, dateTo]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Apply a quick range preset. "all" clears both dates so the backend
  // returns every document ever uploaded (unbounded).
  const applyRangePreset = useCallback((preset: '7d' | '30d' | 'all') => {
    if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
      return;
    }
    const { from, to } = getLastNDaysRange(preset === '7d' ? 7 : 30);
    setDateFrom(from);
    setDateTo(to);
  }, []);

  // Which preset (if any) the current From/To pair corresponds to, so we can
  // highlight the active button and show a friendly "Showing …" label.
  const activePreset: '7d' | '30d' | 'all' | 'custom' = useMemo(() => {
    if (!dateFrom && !dateTo) return 'all';
    const r7 = getLastNDaysRange(7);
    if (dateFrom === r7.from && dateTo === r7.to) return '7d';
    const r30 = getLastNDaysRange(30);
    if (dateFrom === r30.from && dateTo === r30.to) return '30d';
    return 'custom';
  }, [dateFrom, dateTo]);

  const handleFileUpload = useCallback(async (file: File, documentName: string) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentName', documentName);

      const response = await apiService.uploadCustomerDocument(customer._id!, formData);

      if (response.success) {
        await fetchDocuments();
        onDocumentsUpdated?.();
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Please try again'}`);
    } finally {
      setUploading(false);
    }
  }, [customer._id, fetchDocuments, onDocumentsUpdated]);

  const handleFileDelete = async (docId: string) => {
    setDocumentToDelete(docId);
    setDeleteDialogOpen(true);
  };

  const confirmFileDelete = async () => {
    if (!documentToDelete) return;

    try {
      const response = await apiService.deleteCustomerDocument(customer._id!, documentToDelete);
      if (response.success) {
        await fetchDocuments();
        onDocumentsUpdated?.();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    } finally {
      setDocumentToDelete(null);
    }
  };

  const handleToggleDocumentSelection = (docId: string) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDocuments.size === filteredDocuments.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(filteredDocuments.map(doc => doc._id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedDocuments.size === 0) {
      alert('Please select at least one document to delete.');
      return;
    }

    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    let deletedCount = 0;
    let failedCount = 0;

    try {
      for (const docId of Array.from(selectedDocuments)) {
        try {
          const response = await apiService.deleteCustomerDocument(customer._id!, docId);
          if (response.success) {
            deletedCount++;
          } else {
            failedCount++;
          }
        } catch (error) {
          console.error(`Error deleting document ${docId}:`, error);
          failedCount++;
        }
      }

      // Clear selection
      setSelectedDocuments(new Set());

      // Refresh documents
      await fetchDocuments();
      onDocumentsUpdated?.();

      // Show result
      if (failedCount > 0) {
        alert(`Deleted ${deletedCount} document(s). Failed to delete ${failedCount} document(s).`);
      } else {
        alert(`Successfully deleted ${deletedCount} document(s).`);
      }
    } catch (error) {
      console.error('Error during bulk delete:', error);
      alert('An error occurred during bulk delete. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddDocumentRequirement = async () => {
    if (!newDocName.trim()) {
      alert('Please enter a document name');
      return;
    }

    try {
      const response = await apiService.addDocumentRequirement(customer._id!, newDocName.trim());
      if (response.success && response.data) {
        setDocumentsRequired(response.data.documentsRequired);
        setNewDocName('');
        setAddingNew(false);
        onDocumentsUpdated?.();
      } else {
        throw new Error(response.message || 'Failed to add document requirement');
      }
    } catch (error) {
      console.error('Error adding document requirement:', error);
      alert(`Failed to add: ${error instanceof Error ? error.message : 'Please try again'}`);
    }
  };

  const handleStartEdit = (index: number, currentName: string) => {
    setEditingIndex(index);
    setEditingValue(currentName);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleSaveEdit = async (index: number) => {
    if (!editingValue.trim()) {
      alert('Document name cannot be empty');
      return;
    }

    if (editingValue.trim() === documentsRequired[index]) {
      // No change
      handleCancelEdit();
      return;
    }

    try {
      const response = await apiService.renameDocumentRequirement(customer._id!, index, editingValue.trim());
      if (response.success && response.data) {
        setDocumentsRequired(response.data.documentsRequired);
        await fetchDocuments(); // Refresh to get updated document names
        handleCancelEdit();
        onDocumentsUpdated?.();
      } else {
        throw new Error(response.message || 'Failed to rename document requirement');
      }
    } catch (error) {
      console.error('Error renaming document requirement:', error);
      alert(`Failed to rename: ${error instanceof Error ? error.message : 'Please try again'}`);
    }
  };

  const handleDeleteDocumentRequirement = async (index: number) => {
    const documentName = documentsRequired[index];
    if (!window.confirm(`Are you sure you want to delete "${documentName}"? This can only be done if no documents are uploaded for it.`)) {
      return;
    }

    try {
      const response = await apiService.deleteDocumentRequirement(customer._id!, index);
      if (response.success && response.data) {
        setDocumentsRequired(response.data.documentsRequired);
        onDocumentsUpdated?.();
      } else {
        throw new Error(response.message || 'Failed to delete document requirement');
      }
    } catch (error) {
      console.error('Error deleting document requirement:', error);
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Please try again'}`);
    }
  };

  const handleDownloadAll = async () => {
    if (documents.length === 0) {
      alert('No documents available to download');
      return;
    }

    try {
      setDownloading(true);
      await apiService.downloadAllDocuments(customer._id!);
    } catch (error) {
      console.error('Error downloading all documents:', error);
      alert(`Failed to download: ${error instanceof Error ? error.message : 'Please try again'}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent, documentName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(documentName);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, documentName: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(null);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        // Upload all files
        files.forEach(file => {
          handleFileUpload(file, documentName);
        });
      }
    },
    [handleFileUpload]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, documentName: string) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Upload all selected files
      Array.from(files).forEach(file => {
        handleFileUpload(file, documentName);
      });
    }
  };

  const getDocumentsForName = (documentName: string): CustomerDocument[] => {
    return documents.filter((doc) => doc.name === documentName);
  };

  // The backend already applies the date-range filter and returns documents
  // newest-first (see fetchDocuments); this only applies the search filter,
  // which stays client-side for instant-as-you-type filtering without a refetch.
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter((doc) =>
      doc.originalName.toLowerCase().includes(query) ||
      doc.name.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  // Get filtered documents for a specific document name
  const getFilteredDocumentsForName = (documentName: string): CustomerDocument[] => {
    return filteredDocuments.filter((doc) => doc.name === documentName);
  };

  // Check if a document requirement has any matching documents after filtering
  const hasMatchingDocuments = (documentName: string): boolean => {
    return filteredDocuments.some((doc) => doc.name === documentName);
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Highlight search matches in text
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-gray-500">Loading documents...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle>Required Documents ({documents.length} total)</CardTitle>
            <CardDescription>
              Manage document requirements and upload files below.
              {user?.role !== 'super-admin' && user?.role !== 'admin' && (
                <span className="block text-xs text-amber-600 mt-1">
                  You can only view documents you have uploaded. Admin and Super-admin can see all documents.
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {documents.length > 0 && (
              <>
                {/* Bulk Selection Controls */}
                {user?.role === 'super-admin' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedDocuments.size === filteredDocuments.length && filteredDocuments.length > 0 ? (
                        <CheckSquare className="h-4 w-4 mr-2" />
                      ) : (
                        <Square className="h-4 w-4 mr-2" />
                      )}
                      {selectedDocuments.size > 0 ? `${selectedDocuments.size} Selected` : 'Select All'}
                    </Button>
                    {selectedDocuments.size > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={isDeleting}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {isDeleting ? 'Deleting...' : `Delete ${selectedDocuments.size}`}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllDocuments(!showAllDocuments)}
                >
                  <FolderOpen className="h-4 w-4 mr-2" />
                  {showAllDocuments ? 'Show by Type' : 'View All'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  {downloading ? 'Downloading...' : 'Download All'}
                </Button>
              </>
            )}
            {/* Only show Add Document Type button for super-admin, admin, and operator */}
            {user?.role !== 'customer' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddingNew(true)}
                disabled={addingNew}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Document Type
              </Button>
            )}
          </div>
        </div>
        {/* Search Bar and Filters — always rendered (even when the current
            range has no documents) so the customer can always widen the range
            to find older uploads. */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search documents by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Quick range presets */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <Button
              variant={activePreset === '7d' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyRangePreset('7d')}
            >
              Last 7 days
            </Button>
            <Button
              variant={activePreset === '30d' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyRangePreset('30d')}
            >
              Last 30 days
            </Button>
            <Button
              variant={activePreset === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyRangePreset('all')}
            >
              All time
            </Button>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2 flex-1">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To:</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="flex-1"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
                className="whitespace-nowrap"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Dates
              </Button>
            )}
          </div>

          {/* Current-view label so the customer knows what window they're seeing */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">Showing:</span>
            {activePreset === 'all' ? (
              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">All uploads (any date)</span>
            ) : activePreset === '7d' ? (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Last 7 days (default)</span>
            ) : activePreset === '30d' ? (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Last 30 days</span>
            ) : (
              <>
                {dateFrom && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                    From: {new Date(dateFrom).toLocaleDateString('en-GB')}
                  </span>
                )}
                {dateTo && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                    To: {new Date(dateTo).toLocaleDateString('en-GB')}
                  </span>
                )}
                {!dateFrom && !dateTo && (
                  <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">All uploads (any date)</span>
                )}
              </>
            )}
            {searchQuery && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Search: "{searchQuery}"
              </span>
            )}
            <span className="text-gray-500">
              ({filteredDocuments.length} of {documents.length} in this range)
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Empty-range hint — the current date window has no uploads, but the
            customer may have older documents. Nudge them to widen the range so
            they don't think their data is gone. */}
        {documents.length === 0 && !loading && activePreset !== 'all' && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-amber-800 font-medium">
              No documents found in this date range.
            </p>
            <p className="text-sm text-amber-700 mt-1">
              You're viewing {activePreset === '7d' ? 'the last 7 days' : activePreset === '30d' ? 'the last 30 days' : 'a limited date range'} by default.
              Older documents you uploaded may fall outside this window.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => applyRangePreset('all')}
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Show all my uploads
            </Button>
          </div>
        )}

        {/* Add New Document Requirement */}
        {addingNew && (
          <div className="border-2 border-dashed border-blue-500 rounded-lg p-4 bg-blue-50">
            <div className="flex items-center gap-2">
              <Input
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="Enter document name (e.g., Aadhaar Card)"
                className="flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddDocumentRequirement();
                  } else if (e.key === 'Escape') {
                    setAddingNew(false);
                    setNewDocName('');
                  }
                }}
              />
              <Button
                size="sm"
                onClick={handleAddDocumentRequirement}
                disabled={!newDocName.trim()}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAddingNew(false);
                  setNewDocName('');
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {searchQuery && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Found <strong>{filteredDocuments.length}</strong> document(s) matching "{searchQuery}"
            </p>
          </div>
        )}

        {/* View All Documents Section */}
        {showAllDocuments && filteredDocuments.length > 0 && (
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-4">All Uploaded Documents</h3>
            <div className="space-y-2">
              {filteredDocuments.map((doc) => {
                const isSuperAdmin = user?.role === 'super-admin';
                return (
                  <div
                    key={doc._id}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Checkbox for bulk selection (super-admin only) */}
                      {isSuperAdmin && (
                        <button
                          onClick={() => handleToggleDocumentSelection(doc._id)}
                          className="flex-shrink-0 hover:bg-gray-200 p-1 rounded"
                        >
                          {selectedDocuments.has(doc._id) ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      )}
                      <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {searchQuery ? highlightText(doc.originalName, searchQuery) : doc.originalName}
                        </p>
                        <p className="text-xs text-gray-500">
                          <span className="font-medium text-blue-600">{doc.name}</span> • {formatFileSize(doc.fileSize)} • {formatDate(doc.uploadedAt)}
                          {doc.uploadedBy && ` • by ${doc.uploadedBy.name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {doc.s3Url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(doc.s3Url, '_blank')}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFileDelete(doc._id)}
                          className="hover:bg-red-50 hover:border-red-200"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Existing Document Requirements */}
        {!showAllDocuments && documentsRequired.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            No document requirements yet. Click "Add Document Type" to get started.
          </p>
        )}

        {!showAllDocuments && documentsRequired.length > 0 && (
          documentsRequired.map((documentName, index) => {
            const uploadedDocs = getDocumentsForName(documentName);
            const filteredUploadedDocs = getFilteredDocumentsForName(documentName);
            const isDraggedOver = dragOver === documentName;
            const isEditing = editingIndex === index;

            // Hide sections with no matching documents when searching
            if (searchQuery && !hasMatchingDocuments(documentName)) {
              return null;
            }

            return (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  {isEditing ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="flex-1 max-w-md"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit(index);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleSaveEdit(index)}
                        disabled={!editingValue.trim()}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{documentName}</h3>
                        {/* Only show edit button for non-customer roles */}
                        {user?.role !== 'customer' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEdit(index, documentName)}
                            className="h-7 w-7 p-0"
                            title="Edit name"
                          >
                            <Edit2 className="h-3.5 w-3.5 text-gray-500" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {uploadedDocs.length} uploaded
                          {user?.role !== 'super-admin' && user?.role !== 'admin' && uploadedDocs.length > 0 && (
                            <span className="text-xs text-amber-600 ml-1">(yours)</span>
                          )}
                        </span>
                        {/* Only show delete button for non-customer roles */}
                        {user?.role !== 'customer' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocumentRequirement(index)}
                            className="h-7 w-7 p-0 hover:bg-red-50"
                            title={uploadedDocs.length > 0 ? "Cannot delete - documents uploaded" : "Delete requirement"}
                            disabled={uploadedDocs.length > 0}
                          >
                            <Trash2 className={`h-3.5 w-3.5 ${uploadedDocs.length > 0 ? 'text-gray-300' : 'text-red-600'}`} />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Upload Area */}
                {!isEditing && (
                  <>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDraggedOver
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      onDragOver={(e) => handleDragOver(e, documentName)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, documentName)}
                    >
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">
                        Drag and drop your files here, or click to browse
                      </p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC, ZIP Max (100 Mb) each file</p>
                      <input
                        type="file"
                        id={`file-${index}`}
                        className="hidden"
                        onChange={(e) => handleFileInputChange(e, documentName)}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip"
                        disabled={uploading}
                        multiple
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => document.getElementById(`file-${index}`)?.click()}
                        disabled={uploading}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </Button>
                    </div>

                    {/* Uploaded Documents */}
                    {filteredUploadedDocs.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">
                          Uploaded Files:
                          {searchQuery && uploadedDocs.length !== filteredUploadedDocs.length && (
                            <span className="text-xs text-gray-500 ml-2">
                              (showing {filteredUploadedDocs.length} of {uploadedDocs.length})
                            </span>
                          )}
                        </p>
                        {filteredUploadedDocs.map((doc) => {
                          const isSuperAdmin = user?.role === 'super-admin';
                          return (
                            <div
                              key={doc._id}
                              className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                {/* Checkbox for bulk selection (super-admin only) */}
                                {isSuperAdmin && (
                                  <button
                                    onClick={() => handleToggleDocumentSelection(doc._id)}
                                    className="flex-shrink-0 hover:bg-gray-200 p-1 rounded"
                                  >
                                    {selectedDocuments.has(doc._id) ? (
                                      <CheckSquare className="h-5 w-5 text-blue-600" />
                                    ) : (
                                      <Square className="h-5 w-5 text-gray-400" />
                                    )}
                                  </button>
                                )}
                                <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {searchQuery ? highlightText(doc.originalName, searchQuery) : doc.originalName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(doc.fileSize)} • {formatDate(doc.uploadedAt)}
                                    {doc.uploadedBy && ` • by ${doc.uploadedBy.name}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {doc.s3Url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(doc.s3Url, '_blank')}
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleFileDelete(doc._id)}
                                    className="hover:bg-red-50 hover:border-red-200"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog */}
    <ConfirmationDialog
      open={deleteDialogOpen}
      onOpenChange={setDeleteDialogOpen}
      onConfirm={confirmFileDelete}
      title="Delete Document"
      description="Are you sure you want to delete this document? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive={true}
    />

    {/* Bulk Delete Confirmation Dialog */}
    <ConfirmationDialog
      open={bulkDeleteDialogOpen}
      onOpenChange={setBulkDeleteDialogOpen}
      onConfirm={confirmBulkDelete}
      title="Delete Multiple Documents"
      description={`Are you sure you want to delete ${selectedDocuments.size} document(s)? This action cannot be undone.`}
      confirmText="Delete All"
      cancelText="Cancel"
      destructive={true}
    />
    </>
  );
};

export default DocumentsSection;
